import React from 'react';
import { type UserVideo } from '@/api/videoApi';
import SimpleVideoThumbnail from '@/components/admin/gallery/SimpleVideoThumbnail';
import { VideoRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import { useResponsiveColumns, VIDEO_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import VirtualizedGrid from '@/components/ui/common/VirtualizedGrid';

interface VirtualizedVideoGridProps {
  /** Array of videos to display */
  videos: UserVideo[];
  /** Whether initial loading is in progress */
  isLoading?: boolean;
  /** Whether more items are being loaded */
  isLoadingMore?: boolean;
  /** Handler for video click */
  onVideoClick: (video: UserVideo) => void;
  /** Handler for pin video */
  // onPinVideo?: (video: UserVideo) => void;
  /** Handler for unpin video */
  // onUnpinVideo?: (video: UserVideo) => void;
  /** Function to get status badge */
  getStatusBadge: (status?: string) => React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Whether this is a pinned section */
  isPinnedSection?: boolean;
  /** Whether this is the favourites page (always show solid heart) */
  isFavouritesPage?: boolean;
  /** Whether this is the archives page (hide pin button) */
  isArchivesPage?: boolean;
  /** Handler for favourite toggle success */
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  /** Handler for archive toggle success */
  // onArchiveSuccess?: (isArchived: boolean) => void;
  /** Whether the current user is an admin (controls archive button visibility) */
  // isAdmin?: boolean;
}

/**
 * Virtualized video grid component for handling large numbers of videos
 * Uses window-based virtualization to only render visible videos
 * Optimized for galleries with 100k+ videos
 * 
 * Used by both dashboard and admin content review pages.
 * 
 * Features:
 * - Only renders visible rows plus buffer
 * - Smooth scrolling with overscan
 * - Skeleton loading states
 * - Responsive column layout
 * - Memory efficient for massive datasets
 */
const VirtualizedVideoGrid: React.FC<VirtualizedVideoGridProps> = ({
  videos,
  isLoading = false,
  isLoadingMore = false,
  onVideoClick,
  // onPinVideo,
  // onUnpinVideo,
  getStatusBadge,
  className = '',
  isPinnedSection = false,
  isFavouritesPage = false,
  isArchivesPage = false,
  onFavouriteSuccess,
  // onArchiveSuccess,
  // isAdmin = false,
}) => {
  const columns = useResponsiveColumns(VIDEO_GRID_COLUMNS);

  // Show skeleton grid during initial loading
  if (isLoading && videos.length === 0) {
    return (
      <div className={className}>
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <VideoRowSkeleton key={`skeleton-row-${isPinnedSection}-${i}`} columns={columns} rowIndex={i} />
          ))}
        </div>
      </div>
    );
  }

  // No videos to display
  if (videos.length === 0) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      <VirtualizedGrid
        items={videos}
        columns={columns}
        estimatedRowHeight={264} // ~240px for 16:9 card + 24px gap
        emptyAspectRatio="16/9"
        overscan={15}
        renderItem={(video) => (
          <SimpleVideoThumbnail
            video={video}
            onVideoClick={onVideoClick}
            // do onPinVideo={onPinVideo}
            // onUnpinVideo={onUnpinVideo}
            getStatusBadge={getStatusBadge}
            // isPinnedSection={isPinnedSection}
            isFavouritesPage={isFavouritesPage}
            isArchivesPage={isArchivesPage}
            onFavouriteSuccess={onFavouriteSuccess}
            // do onArchiveSuccess={onArchiveSuccess}
            // isAdmin={isAdmin}
          />
        )}
        getItemKey={(video) => video.id}
      />

      {/* Loading more indicator with skeleton */}
      {isLoadingMore && (
        <div className="mt-4">
          <VideoRowSkeleton columns={columns} />
        </div>
      )}
    </div>
  );
};

export default VirtualizedVideoGrid;
